import { useEffect, useRef } from "react";
import type { CtcaeTerm } from "../types";
import { GradeCards } from "./GradeCards";
import { SourceText } from "./SourceText";

type Props = {
  history: CtcaeTerm[];
  onBack: () => void;
  onClose: () => void;
  onOpenReference: (id: string) => void;
  onOpenPdf: (record: CtcaeTerm) => void;
};

export function TermDialog({ history, onBack, onClose, onOpenReference, onOpenPdf }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const record = history.at(-1);
  useEffect(() => {
    if (!record) return;
    const previous = document.activeElement as HTMLElement | null;
    document.body.classList.add("modal-open");
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [record, onClose]);
  if (!record) return null;
  return (
    <div className="modal-layer" role="presentation">
      <button className="modal-backdrop" type="button" aria-label="Close referenced term" onClick={onClose} />
      <section className="term-modal" role="dialog" aria-modal="true" aria-labelledby="reference-title">
        <header className="modal-header">
          {history.length > 1 ? <button className="back-button" type="button" onClick={onBack}>← Back</button> : <span />}
          <span>Referenced term</span>
          <button className="icon-button" type="button" ref={closeRef} aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="modal-content">
          <span className="eyebrow">CTCAE v{record.version}</span>
          <h2 id="reference-title">{record.term}</h2>
          <p className="modal-soc">{record.soc}</p>
          <GradeCards record={record} />
          <section className="modal-source-block"><h3>Definition</h3><p><SourceText text={record.definition} /></p></section>
          {record.navigationalNote && (
            <section className="modal-source-block"><h3>Navigational note</h3><p><SourceText text={record.navigationalNote} crossReferences={record.crossReferences} onOpenReference={onOpenReference} /></p></section>
          )}
          <button className="source-link" type="button" onClick={() => onOpenPdf(record)}>
            <span><small>Source</small>CTCAE v{record.version} · p. {record.source.page}</span>
            <strong>View PDF</strong>
          </button>
        </div>
      </section>
    </div>
  );
}
